import { useState } from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Icon } from '@/common/ui/Icon'
import { Card, type GenerateCardProps } from '../Card/Card'
import { useCollapsibleCardGroup } from './useCollapsibleCardGroup'
import './collapsible-card.css'

type CollapsibleCardProps = Omit<GenerateCardProps, 'children' | 'title'> & {
    title: React.ReactNode
    /** Доп. контент в шапке (например миниатюры) — между заголовком и стрелкой */
    headerPreview?: React.ReactNode
    /** Действие в шапке — всегда видимо, рядом со стрелкой */
    headerAction?: React.ReactNode
    children: React.ReactNode
    defaultOpen?: boolean
    isOpen?: boolean
    onToggle?: (isOpen: boolean) => void
    ariaLabel?: string
    contentClassName?: string
    groupItemId?: string
    description?: string
    disableAnimation?: boolean
}

export const CollapsibleCard = ({
    title,
    headerPreview,
    headerAction,
    children,
    defaultOpen = false,
    isOpen,
    onToggle,
    ariaLabel,
    contentClassName,
    groupItemId,
    description,
    disableAnimation = false,
    ...cardProps
}: CollapsibleCardProps) => {
    const group = useCollapsibleCardGroup()
    const groupedContext = group && groupItemId ? group : null
    const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen)
    const isControlled = typeof isOpen === 'boolean'
    const isGrouped = Boolean(groupedContext)
    const currentIsOpen = isControlled
        ? isOpen
        : isGrouped
          ? groupedContext?.openItemId === groupItemId
          : internalIsOpen

    const handleToggle = () => {
        const nextIsOpen = !currentIsOpen

        if (!isControlled && groupedContext && groupItemId) {
            groupedContext.setHasInteracted(true)
            groupedContext.setOpenItemId(nextIsOpen ? groupItemId : null)
        }

        if (!isControlled && !isGrouped) {
            setInternalIsOpen(nextIsOpen)
        }

        onToggle?.(nextIsOpen)
    }

    const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return
        }

        event.preventDefault()
        handleToggle()
    }

    return (
        <Card
            {...cardProps}
            className={cn('collapsible-card card-product', {
                'collapsible-card--no-animation': disableAnimation,
            })}
            cardBodyClassName='collapsible-card__body'
        >
            <div
                role='button'
                tabIndex={0}
                className={cn('collapsible-card__trigger', {
                    'is-closed': !currentIsOpen,
                })}
                onClick={handleToggle}
                onKeyDown={handleTriggerKeyDown}
                aria-expanded={currentIsOpen}
                aria-label={ariaLabel ?? 'Переключить карточку'}
            >
                <div className='collapsible-card__header'>
                    <div className='collapsible-card__title-wrapper'>
                        <div className='collapsible-card__title'>{title}</div>
                        {description && <div className='collapsible-card__title-description'>{description}</div>}
                    </div>
                    {headerPreview ? (
                        <div
                            className={cn('collapsible-card__preview transition-opacity duration-200', {
                                'opacity-100': !currentIsOpen,
                                'opacity-0': currentIsOpen,
                            })}
                        >
                            {headerPreview}
                        </div>
                    ) : null}
                </div>
                <div className='collapsible-card__action'>
                    {headerAction}
                    <div className='collapsible-card__icon-wrap'>
                        <Icon
                            name='arrow-down'
                            className={cn('collapsible-card__icon', {
                                'is-open': currentIsOpen,
                            })}
                        />
                    </div>
                </div>
            </div>

            <div
                className={cn('collapsible-card__collapse', {
                    'is-open': currentIsOpen,
                    'is-closed': !currentIsOpen,
                })}
                aria-hidden={!currentIsOpen}
            >
                <div className='collapsible-card__collapse-inner'>
                    <div className={cn('collapsible-card__content', contentClassName)}>{children}</div>
                </div>
            </div>
        </Card>
    )
}
